import { create } from "@bufbuild/protobuf";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { keys as userQueryKeys, type PlainUser } from "@/queries/user-queries";
import {
    type CreateIntegrationRequest,
    type UpdateIntegrationRequest,
    type DeleteIntegrationRequest,
    type Integration,
    ValidateIntegrationRequestSchema,
} from "gitpod-next-api/gitpod/v1/integration_pb";

// Query key factories
export const integrationDefinitionsQueryKey = () => ["integration-definitions"];
export const integrationsQueryKey = (organizationId?: string) => ["integrations", organizationId].filter(Boolean);
export const integrationQueryKey = (integrationId: string) => ["integration", integrationId];
export const integrationValidationQueryKey = (integrationId: string) => ["integration-validation", integrationId];

// List integration definitions
export const useListIntegrationDefinitionsOptions = () => {
    const api = useGitpodAPI();
    return queryOptions({
        queryKey: integrationDefinitionsQueryKey(),
        queryFn: async () => {
            const response = await api.integrationService.listIntegrationDefinitions({});
            return response.definitions;
        },
    });
};

export const useListIntegrationDefinitions = () => {
    return useQuery(useListIntegrationDefinitionsOptions());
};

// List integrations for organization
export const useListIntegrationsOptions = (organizationId?: string) => {
    const api = useGitpodAPI();
    return queryOptions({
        queryKey: integrationsQueryKey(organizationId),
        queryFn: async () => {
            if (!organizationId) {
                return [];
            }
            const response = await api.integrationService.listIntegrations({});
            return response.integrations;
        },
        enabled: !!organizationId,
    });
};

export const useListIntegrations = (organizationId?: string) => {
    return useQuery(useListIntegrationsOptions(organizationId));
};

// Get a single integration by ID
export const useGetIntegrationOptions = (integrationId?: string) => {
    const api = useGitpodAPI();
    return queryOptions({
        queryKey: integrationQueryKey(integrationId ?? ""),
        queryFn: async () => {
            const response = await api.integrationService.getIntegration({ id: integrationId });
            return response.integration;
        },
        enabled: !!integrationId,
    });
};

export const useGetIntegration = (integrationId?: string) => {
    return useQuery(useGetIntegrationOptions(integrationId));
};

// Validate integration configuration.
// When pendingReauth is true the cached result is treated as immediately stale
// and every window-focus triggers a refetch, so the UI picks up the new OAuth
// state as soon as the user returns from the provider's authorization page.
export const useValidateIntegrationOptions = (integrationId?: string, opts?: { pendingReauth?: boolean }) => {
    const api = useGitpodAPI();
    return queryOptions({
        queryKey: integrationValidationQueryKey(integrationId ?? ""),
        queryFn: async () => {
            const response = await api.integrationService.validateIntegration(
                create(ValidateIntegrationRequestSchema, { integrationId }),
            );
            return response;
        },
        enabled: !!integrationId,
        // During a pending reauth we want every focus event to re-validate.
        // Otherwise cache for 5 minutes to avoid hitting the provider API on
        // every tab focus. The query cache is in-memory, so a full page reload
        // always fetches fresh.
        staleTime: opts?.pendingReauth ? 0 : 5 * 60 * 1000,
        refetchOnWindowFocus: opts?.pendingReauth ? "always" : true,
    });
};

export const useValidateIntegration = (integrationId?: string, opts?: { pendingReauth?: boolean }) => {
    return useQuery(useValidateIntegrationOptions(integrationId, opts));
};

// Create integration mutation
export const useCreateIntegration = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: CreateIntegrationRequest) => {
            const response = await api.integrationService.createIntegration(request);
            return response.integration;
        },
        onSuccess: () => {
            const user = queryClient.getQueryData<PlainUser>(userQueryKeys.getAuthenticatedUserQueryKey());
            // Invalidate integrations list
            void queryClient.invalidateQueries({
                queryKey: integrationsQueryKey(user?.organizationId),
            });
        },
    });
};

// Update integration mutation
export const useUpdateIntegration = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: UpdateIntegrationRequest) => {
            const response = await api.integrationService.updateIntegration(request);
            return response.integration;
        },
        onSuccess: () => {
            const user = queryClient.getQueryData<PlainUser>(userQueryKeys.getAuthenticatedUserQueryKey());
            // Invalidate integrations list
            void queryClient.invalidateQueries({
                queryKey: integrationsQueryKey(user?.organizationId),
            });
        },
    });
};

// Delete integration mutation
export const useDeleteIntegration = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: DeleteIntegrationRequest) => {
            await api.integrationService.deleteIntegration(request);
        },
        onSuccess: () => {
            const user = queryClient.getQueryData<PlainUser>(userQueryKeys.getAuthenticatedUserQueryKey());
            // Invalidate integrations list
            void queryClient.invalidateQueries({
                queryKey: integrationsQueryKey(user?.organizationId),
            });
        },
    });
};

export type MCPIntegration = Integration;

export const useListMCPIntegrations = (organizationId?: string) => {
    const { data: integrations, isLoading } = useListIntegrations(organizationId);

    return useMemo(() => {
        if (isLoading) {
            return { data: [], isLoading: true };
        }

        const mcpIntegrations: MCPIntegration[] = integrations?.filter((i) => i.enabled && i.capabilities?.mcp) || [];

        return { data: mcpIntegrations, isLoading: false };
    }, [integrations, isLoading]);
};

export type MCPErrorSnapshot = {
    statusVersion: string;
    authTokenId: string | undefined;
};

export function isErrorSuppressed(
    snapshot: MCPErrorSnapshot | undefined,
    currentStatusVersion: string,
    currentAuthTokenId: string | undefined,
): boolean {
    return !!snapshot && snapshot.statusVersion === currentStatusVersion && snapshot.authTokenId !== currentAuthTokenId;
}
